import type { AxiosError } from 'axios';

/**
 * `axios.isAxiosError`, re-derived so this module can stay a type-only
 * dependant of axios.
 *
 * A value import of `axios` here pulls the whole HTTP client into every bundle
 * that touches the logger door — including the Next.js **edge** bundle, since
 * `src/middleware.ts` reaches this file through `@/utils/logger/logger`. Axios
 * uses `setImmediate` and `process.nextTick`, neither of which the edge runtime
 * supports, so the dev server and every build logged an unsupported-API warning
 * for a predicate that is one property read.
 *
 * This is the same test axios performs: `isObject(payload) &&
 * payload.isAxiosError === true` (`axios/lib/helpers/isAxiosError.js`).
 */
export const isAxiosError = (error: unknown): error is AxiosError =>
  typeof error === 'object' &&
  error !== null &&
  (error as { isAxiosError?: unknown }).isAxiosError === true;

/**
 * PHI-safe projection of an unknown error for Datadog logging.
 *
 * The logger's Datadog transport does NOT apply pino's `pinoRedactedPaths`
 * redaction (redaction runs only on the local pino output), and an axios error
 * carries the raw request/response — the URL and response body can contain
 * patient data. Error-level logs are forwarded to Datadog in prod, so logging
 * the raw error would export PHI unredacted.
 *
 * We therefore log only non-PHI diagnostic fields: the HTTP status (a number)
 * and the axios error code (a stable enum string like `ERR_NETWORK` /
 * `ECONNABORTED`, useful to distinguish network/timeout from HTTP failures).
 *
 * Note: despite the name, the error `message` is intentionally dropped (it can
 * embed PHI), so this is a projection to safe fields rather than a cleaned copy
 * of the error — a non-axios error sanitises to `{}`.
 *
 * Moved verbatim from `src/utils/logger/sanitizeError.ts` (now a shim) with
 * the session D6 family: it belongs to the logger's PHI contract, and domain
 * packages logging errors need it door-importable.
 */
export function sanitizeError(error: unknown): { status?: number; code?: string } {
  if (isAxiosError(error)) {
    return { status: error.response?.status, code: error.code };
  }
  return {};
}
